import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormAttributeInput, FormAttributeType } from '@eview/core/domain/form/form-attribute';
import {
  VULNERABILITY_DRIVER_KEY,
  VULNERABILITY_ROWS_KEY,
  VulnerabilityRow,
  formatVulnerabilityRowsDisplay,
  isRepeatableRowsAttribute,
  parseVulnerabilityRows,
  resolveRowsCatalog,
  vulnerabilityPopulationLabel,
  vulnerabilityRangeLabel
} from '@eview/core/domain/form/vulnerability-rows';
import { Post, PostMediaType } from '@eview/core/domain/post/post';
import { GMapComponent } from '@eview/features/ui/map/gmap.component';
import { Store } from '@ngrx/store';
import { AppState } from '@eview/core/store/states/app.state';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Component({
  selector: 'eview-post-map-view',
  templateUrl: 'post-map-view.component.html',
  styleUrls: ['post-map-view.component.scss']
})
export class PostMapViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public post: Post;
  @Input() private isMapDisabled: Boolean;
  @ViewChild(GMapComponent, { static: false }) childRef: GMapComponent;
  @Output() public onRefreshClick: EventEmitter<any> = new EventEmitter();
  @Output() public onEditClick: EventEmitter<any> = new EventEmitter();
  @Output() public onDownloadMediaClick: EventEmitter<any> = new EventEmitter();
  @Output() public onClose: EventEmitter<any> = new EventEmitter();
  @Output() public onDelete: EventEmitter<any> = new EventEmitter();

  FormAttributeType = FormAttributeType;
  FormAttributeInput = FormAttributeInput;
  PostMediaType = PostMediaType;
  VULNERABILITY_ROWS_KEY = VULNERABILITY_ROWS_KEY;
  mediaUrls: any = {};
  isUserLoggedIn$: Observable<boolean>;
  hideLocationMap = false;

  /** key → parsed rows for vulnerability fields */
  private vulnRowsByKey = new Map<string, VulnerabilityRow[]>();

  constructor(
    private store: Store<AppState>,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.isUserLoggedIn$ = this.store.select(selectUser).pipe(
      map(user => !!user)
    );
    this.rebuildVulnCache();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['post']) {
      this.rebuildVulnCache();
    }
  }

  ngOnDestroy() {}

  isVulnField(value: {
    key?: string;
    label?: string;
    type?: string;
    input?: string;
    config?: any[];
    value?: unknown;
  }): boolean {
    if (value?.key === VULNERABILITY_DRIVER_KEY) {
      return false;
    }
    if (value?.type === FormAttributeType.Tags || value?.input === 'tags') {
      return false;
    }
    if (isRepeatableRowsAttribute(value)) {
      return true;
    }
    const label = (value?.label || '').toLowerCase().trim();
    if (
      label === 'poblaciones en situación de vulnerabilidad' ||
      (label.startsWith('poblaciones en situación de vulnerabilidad') &&
        !label.includes('se identifica'))
    ) {
      return true;
    }
    return this.looksLikeVulnJson(value?.value);
  }

  rowsFor(value: {
    key?: string;
    vulnerabilityRows?: VulnerabilityRow[];
    value?: unknown;
  }): VulnerabilityRow[] {
    if (value?.key && this.vulnRowsByKey.has(value.key)) {
      return this.vulnRowsByKey.get(value.key);
    }
    if (value?.vulnerabilityRows?.length) {
      return value.vulnerabilityRows;
    }
    return parseVulnerabilityRows(value?.value);
  }

  /** Never show raw JSON for vulnerability (or any text attr that is vuln JSON). */
  formatAttrValue(value: {
    key?: string;
    label?: string;
    type?: string;
    input?: string;
    vulnerabilityRows?: VulnerabilityRow[];
    vulnerabilityDisplay?: string;
    value?: unknown;
  }): string {
    if (this.isVulnField(value)) {
      const catalog = resolveRowsCatalog((value as any)?.config);
      const rows = this.rowsFor(value);
      if (rows.length) {
        return formatVulnerabilityRowsDisplay(
          rows,
          localStorage.getItem('languageCode'),
          catalog
        );
      }
      if (value.vulnerabilityDisplay) {
        return value.vulnerabilityDisplay;
      }
      const formatted = formatVulnerabilityRowsDisplay(
        value?.value,
        localStorage.getItem('languageCode'),
        catalog
      );
      if (formatted) {
        return formatted;
      }
    }
    const raw = value?.value;
    if (Array.isArray(raw)) {
      const first = raw[0];
      if (first == null) {
        return '';
      }
      // Avoid "[object Object]" for lat/lon, tags, etc.
      if (typeof first === 'object') {
        return '';
      }
      return String(first);
    }
    if (raw != null && typeof raw === 'object') {
      return '';
    }
    return raw != null ? String(raw) : '';
  }

  populationLabel(row: VulnerabilityRow, fa?: { config?: any[] }): string {
    const catalog = resolveRowsCatalog(fa?.config);
    return vulnerabilityPopulationLabel(
      row.population,
      localStorage.getItem('languageCode'),
      row.other_text,
      catalog
    );
  }

  rangeLabel(row: VulnerabilityRow, fa?: { config?: any[] }): string {
    const catalog = resolveRowsCatalog(fa?.config);
    return vulnerabilityRangeLabel(
      row.range,
      localStorage.getItem('languageCode'),
      catalog
    );
  }

  downloadMedia(value: any, mediaId: string) {
    this.onDownloadMediaClick.emit({ ...value, value: [mediaId] });
  }

  private looksLikeVulnJson(raw: unknown): boolean {
    const first = Array.isArray(raw) ? raw[0] : raw;
    if (typeof first !== 'string') {
      return false;
    }
    return first.includes('population') && first.includes('range');
  }

  private rebuildVulnCache(): void {
    this.vulnRowsByKey.clear();
    const values = (this.post as any)?.values;
    if (!Array.isArray(values)) {
      return;
    }
    for (const fa of values) {
      if (!fa || !this.isVulnField(fa)) {
        continue;
      }
      const catalog = resolveRowsCatalog(fa.config);
      const rows =
        fa.vulnerabilityRows?.length > 0
          ? fa.vulnerabilityRows
          : parseVulnerabilityRows(fa.value);
      const key = fa.key || VULNERABILITY_ROWS_KEY;
      this.vulnRowsByKey.set(key, rows);
      fa.vulnerabilityRows = rows;
      if (rows.length) {
        fa.vulnerabilityDisplay = formatVulnerabilityRowsDisplay(
          rows,
          localStorage.getItem('languageCode'),
          catalog
        );
        fa.value = [fa.vulnerabilityDisplay];
      }
    }
  }
}
